import React from "react";
import { useLanguage } from "../../context/LanguageContext";
import { translations } from "../../i18n/translations";
import { motion } from "framer-motion";
import {
  FormValidationSummary,
  type FormValidationError,
  useFormValidation,
} from "../common/FormValidationSummary";
import type { FormStepProps } from "../../types/application";
import { isMobile } from "react-device-detect";

// Component for showing extracted data indicator
const ExtractedFieldIndicator: React.FC<{ isExtracted: boolean; t: any }> = ({
  isExtracted,
  t,
}) => {
  if (!isExtracted) return null;

  return (
    <div className="flex items-center text-sm text-green-800 mt-2 font-medium">
      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
          clipRule="evenodd"
        />
      </svg>
      {t.extractedFromResume}
    </div>
  );
};

const normalizeSelectionText = (value: string) =>
  value.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

const splitSelectionValues = (value: unknown): string[] => {
  const values = Array.isArray(value) ? value : value ? [value] : [];

  return values
    .flatMap((item) => String(item).split(/[,;|]/))
    .map((item) => item.trim())
    .filter(Boolean);
};

const getSelectedValues = (
  value: unknown,
  options: Array<{ value: string; aliases?: string[] }>
) => {
  const values = splitSelectionValues(value);
  const normalizedValues = values.map(normalizeSelectionText);
  const normalizedSource = normalizedValues.join(" ");

  return options
    .filter((option) =>
      [option.value, ...(option.aliases || [])]
        .map(normalizeSelectionText)
        .some(
          (alias) =>
            normalizedValues.includes(alias) ||
            normalizedSource.includes(alias)
        )
    )
    .map((option) => option.value);
};

const sleResultOptions = [
  { value: "Reading" },
  { value: "Writing" },
  { value: "Oral" },
  { value: "No" },
];

const languageProfileOptions = [
  {
    value: "English Essential Positions",
    aliases: ["English", "English Essential"],
  },
  {
    value: "French Essential Positions",
    aliases: ["French", "French Essential"],
  },
  {
    value: "Bilingual Positions",
    aliases: ["Bilingual", "Bilingual Essential", "Bilingual Essential Positions"],
  },
];

const LanguageStep: React.FC<FormStepProps> = ({
  data,
  onUpdate,
  onNext,
  onPrevious,
}) => {
  const { language } = useLanguage();
  const t = translations[language] || translations.en;
  const validation = useFormValidation({ t, includeNativeValidation: false });
  const firstOfficialLanguageUrl =
    language === "fr"
      ? "https://www.canada.ca/fr/commission-fonction-publique/emplois/services/emplois-gc/exigences-linguistiques.html"
      : "https://www.canada.ca/en/public-service-commission/jobs/services/gc-jobs/language-requirements-candidates.html";
  const validSLEUrl =
    language === "fr"
      ? "https://www.canada.ca/fr/commission-fonction-publique/services/evaluation-langue-seconde.html"
      : "https://www.canada.ca/en/public-service-commission/services/second-language-testing-public-service.html";

  const handleEnhancedDataChange = (
    field: string,
    value: string | string[]
  ) => {
    const enhancedData = { ...data.enhancedData, [field]: value };
    onUpdate({ enhancedData });
  };

  const handleSLEResultsChange = (test: string, checked: boolean) => {
    const currentResults = getSelectedValues(
      data.enhancedData?.validSLEResults,
      sleResultOptions
    );
    const newResults = checked
      ? Array.from(new Set([...currentResults, test]))
      : currentResults.filter((result) => result !== test);

    handleEnhancedDataChange("validSLEResults", newResults);
  };

  const handleLanguageProfilesChange = (profile: string, checked: boolean) => {
    const currentProfiles = getSelectedValues(
      data.enhancedData?.languageProfilesInterested,
      languageProfileOptions
    );
    const newProfiles = checked
      ? Array.from(new Set([...currentProfiles, profile]))
      : currentProfiles.filter((currentProfile) => currentProfile !== profile);

    handleEnhancedDataChange("languageProfilesInterested", newProfiles);
  };

  const handleSubmit = (e: React.FormEvent) => {
    validation.handleSubmit(e, onNext, getValidationErrors());
  };

  const enhancedData = data.enhancedData || {};
  const extractedFields = data.extractionMetadata?.extractedFields || [];
  const isFieldExtracted = (fieldName: string) =>
    extractedFields.includes(fieldName);
  const validSLEResults = getSelectedValues(
    enhancedData.validSLEResults,
    sleResultOptions
  );
  const languageProfilesInterested = getSelectedValues(
    enhancedData.languageProfilesInterested,
    languageProfileOptions
  );
  const hasSelectedSLE = validSLEResults.length > 0;
  const hasSelectedLanguageProfiles = languageProfilesInterested.length > 0;

  // Show additional fields if has valid SLE results
  const hasValidSLE = hasSelectedSLE;
  const hasActualSLEResults =
    hasValidSLE &&
    !validSLEResults.includes("No");

  const getValidationErrors = (): FormValidationError[] => {
    const errors: FormValidationError[] = [];

    if (!enhancedData.firstOfficialLanguage) {
      errors.push({
        id: isMobile
          ? "firstOfficialLanguage-english"
          : "firstOfficialLanguage-english-desktop",
        label: `15. {t.firstOfficialLanguageLabel}`,
        message: t.formValidationRequiredMessage,
      });
    }

    if (!hasSelectedSLE) {
      errors.push({
        id: isMobile ? "validSLE-reading" : "validSLE-reading-desktop",
        label: `16. {t.validSLELabel}`,
        message: t.formValidationRequiredMessage,
      });
    }

    if (hasActualSLEResults && !enhancedData.sleLevel) {
      errors.push({
        id: isMobile ? "sleLevel" : "sleLevel-desktop",
        label: `17. {t.sleLevelLabel}`,
        message: t.formValidationRequiredMessage,
      });
    }

    if (hasActualSLEResults && !enhancedData.sleExpiryDate) {
      errors.push({
        id: isMobile ? "sleExpiryDate" : "sleExpiryDate-desktop",
        label: `18. {t.sleExpiryDateLabel}`,
        message: t.formValidationRequiredMessage,
      });
    }

    if (!hasSelectedLanguageProfiles) {
      errors.push({
        id: isMobile
          ? "languageProfile-english"
          : "languageProfile-english-desktop",
        label: `19. {t.languageProfilesLabel}`,
        message: t.formValidationRequiredMessage,
      });
    }

    return errors;
  };

  const requiredQuestionText = (question: React.ReactNode) => (
    <>
      <strong className="required text-red-700 mrgn-rght-sm">*</strong>
      <span
        className="field-name text-lg"
        style={{ fontSize: "1.125rem", lineHeight: "1.75rem" }}
      >
        {question}
      </span>
      <strong
        className="required text-red-700 text-lg"
        style={{ fontSize: "1.125rem", lineHeight: "1.75rem" }}
      >
        {" "}
        {t.required}
      </strong>
    </>
  );

  const renderFieldHelp = (fieldId: string, helpText: string) => (
    <p id={`${fieldId}-help`} className="text-sm text-gray-700 mt-1 mb-2">
      {helpText}
    </p>
  );

  if (isMobile)
    return (
      <motion.form
        ref={validation.formRef}
        onSubmit={handleSubmit}
        onChangeCapture={validation.handleChangeCapture}
        className="wb-frmvld space-y-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        noValidate
      >
        <FormValidationSummary
          t={t}
          errors={validation.errors}
          errorSummaryRef={validation.errorSummaryRef}
          formRef={validation.formRef}
        />
        {/* Header */}
        <div className="text-left">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {t.languageSectionTitle}
          </h2>
        </div>

        {/* Section: Language Information */}
        <div className="contact-form-list">
          {/* First Official Language */}
          <div>
            <fieldset className="gc-chckbxrdio">
              <legend className="question-legend">
                {requiredQuestionText(<>15. {t.firstOfficialLanguageLabel}</>)}
              </legend>
              <p className="text-lg text-gray-900 mb-3">
                {t.firstOfficialLanguageLearnMore}{" "}
                <a
                  href={firstOfficialLanguageUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 hover:text-primary-700 underline visited:text-primary-600"
                  aria-label={`${t.firstOfficialLanguageLearnMoreLink} (opens in new window)`}
                >
                  {t.firstOfficialLanguageLearnMoreLink}
                </a>
              </p>
              <ul className="list-unstyled lst-spcd-2">
                <li className="radio">
                  <input
                    type="radio"
                    name="firstOfficialLanguage"
                    id="firstOfficialLanguage-english"
                    value="English"
                    checked={enhancedData.firstOfficialLanguage === "English"}
                    onChange={(e) =>
                      handleEnhancedDataChange(
                        "firstOfficialLanguage",
                        e.target.value
                      )
                    }
                    required
                  />
                  <label htmlFor="firstOfficialLanguage-english">
                    {t.firstOfficialLanguageEnglish}
                  </label>
                </li>
                <li className="radio">
                  <input
                    type="radio"
                    name="firstOfficialLanguage"
                    id="firstOfficialLanguage-french"
                    value="French"
                    checked={enhancedData.firstOfficialLanguage === "French"}
                    onChange={(e) =>
                      handleEnhancedDataChange(
                        "firstOfficialLanguage",
                        e.target.value
                      )
                    }
                    required
                  />
                  <label htmlFor="firstOfficialLanguage-french">
                    {t.firstOfficialLanguageFrench}
                  </label>
                </li>
                <li className="radio">
                  <input
                    type="radio"
                    name="firstOfficialLanguage"
                    id="firstOfficialLanguage-other"
                    value="Other"
                    checked={
                      enhancedData.firstOfficialLanguage === "Other" ||
                      enhancedData.firstOfficialLanguage === "Bilingual"
                    }
                    onChange={(e) =>
                      handleEnhancedDataChange(
                        "firstOfficialLanguage",
                        e.target.value
                      )
                    }
                    required
                  />
                  <label htmlFor="firstOfficialLanguage-other">
                    {t.firstOfficialLanguageOther}
                  </label>
                </li>
              </ul>
            </fieldset>
            <ExtractedFieldIndicator
              isExtracted={isFieldExtracted("firstOfficialLanguage")}
              t={t}
            />
          </div>

          {/* Valid SLE Results */}
          <div>
            <fieldset className="gc-chckbxrdio">
              <legend className="question-legend">
                {requiredQuestionText(<>16. {t.validSLELabel}</>)}
              </legend>
              <p className="text-lg text-gray-900 mb-3">
                {t.validSLELearnMore}{" "}
                <a
                  href={validSLEUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 hover:text-primary-700 underline visited:text-primary-600"
                  aria-label={`${t.validSLELearnMoreLink} (opens in new window)`}
                >
                  {t.validSLELearnMoreLink}
                </a>
              </p>
              <ul className="list-unstyled lst-spcd-2">
                <li className="checkbox">
                  <input
                    type="checkbox"
                    id="validSLE-reading"
                    checked={validSLEResults.includes("Reading")}
                    onChange={(e) =>
                      handleSLEResultsChange("Reading", e.target.checked)
                    }
                  />
                  <label htmlFor="validSLE-reading">{t.validSLEReading}</label>
                </li>
                <li className="checkbox">
                  <input
                    type="checkbox"
                    id="validSLE-writing"
                    checked={validSLEResults.includes("Writing")}
                    onChange={(e) =>
                      handleSLEResultsChange("Writing", e.target.checked)
                    }
                  />
                  <label htmlFor="validSLE-writing">{t.validSLEWriting}</label>
                </li>
                <li className="checkbox">
                  <input
                    type="checkbox"
                    id="validSLE-oral"
                    checked={validSLEResults.includes("Oral")}
                    onChange={(e) =>
                      handleSLEResultsChange("Oral", e.target.checked)
                    }
                  />
                  <label htmlFor="validSLE-oral">{t.validSLEOral}</label>
                </li>
                <li className="checkbox">
                  <input
                    type="checkbox"
                    id="validSLE-no"
                    checked={validSLEResults.includes("No")}
                    onChange={(e) =>
                      handleSLEResultsChange("No", e.target.checked)
                    }
                  />
                  <label htmlFor="validSLE-no">{t.validSLENo}</label>
                </li>
              </ul>
            </fieldset>
            <ExtractedFieldIndicator
              isExtracted={isFieldExtracted("validSLEResults")}
              t={t}
            />
          </div>

          {/* Additional SLE fields if has valid results */}
          {hasValidSLE &&
            !validSLEResults.includes("No") && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                transition={{ duration: 0.3 }}
                className="space-y-6 border-t border-gray-200 pt-6"
                aria-live="polite"
                aria-atomic="true"
              >
                <div>
                  <label
                    htmlFor="sleLevel"
                    className="block text-lg font-semibold text-gray-900 mb-2 required"
                  >
                    17. {t.sleLevelLabel}{" "}
                    <strong className="required">{t.required}</strong>
                  </label>
                  {renderFieldHelp("sleLevel", t.sleLevelPlaceholder)}
                  <input
                    type="text"
                    id="sleLevel"
                    name="sleLevel"
                    value={enhancedData.sleLevel || ""}
                    onChange={(e) =>
                      handleEnhancedDataChange("sleLevel", e.target.value)
                    }
                    className="form-control"
                    aria-describedby="sleLevel-help"
                    required={hasActualSLEResults}
                  />
                  <ExtractedFieldIndicator
                    isExtracted={isFieldExtracted("sleLevel")}
                    t={t}
                  />
                </div>

                <div>
                  <label
                    htmlFor="sleExpiryDate"
                    className="block text-lg font-semibold text-gray-900 mb-2 required"
                  >
                    18. {t.sleExpiryDateLabel}{" "}
                    <strong className="required">{t.required}</strong>
                  </label>
                  <input
                    type="date"
                    id="sleExpiryDate"
                    name="sleExpiryDate"
                    value={enhancedData.sleExpiryDate || ""}
                    onChange={(e) =>
                      handleEnhancedDataChange("sleExpiryDate", e.target.value)
                    }
                    className="form-control"
                    required={hasActualSLEResults}
                  />
                  <ExtractedFieldIndicator
                    isExtracted={isFieldExtracted("sleExpiryDate")}
                    t={t}
                  />
                </div>
              </motion.div>
            )}

          <div>
            <fieldset className="gc-chckbxrdio">
              <legend className="question-legend">
                {requiredQuestionText(<>19. {t.languageProfilesLabel}</>)}
              </legend>
              <ul className="list-unstyled lst-spcd-2">
                <li className="checkbox">
                  <input
                    type="checkbox"
                    id="languageProfile-english"
                    checked={languageProfilesInterested.some((profile) =>
                      profile.toLowerCase().includes("english")
                    )}
                    onChange={(e) =>
                      handleLanguageProfilesChange(
                        "English Essential Positions",
                        e.target.checked
                      )
                    }
                  />
                  <label htmlFor="languageProfile-english">
                    {t.languageProfileEnglish}
                  </label>
                </li>
                <li className="checkbox">
                  <input
                    type="checkbox"
                    id="languageProfile-french"
                    checked={languageProfilesInterested.some((profile) =>
                      profile.toLowerCase().includes("french")
                    )}
                    onChange={(e) =>
                      handleLanguageProfilesChange(
                        "French Essential Positions",
                        e.target.checked
                      )
                    }
                  />
                  <label htmlFor="languageProfile-french">
                    {t.languageProfileFrench}
                  </label>
                </li>
                <li className="checkbox">
                  <input
                    type="checkbox"
                    id="languageProfile-bilingual"
                    checked={languageProfilesInterested.some((profile) =>
                      profile.toLowerCase().includes("bilingual")
                    )}
                    onChange={(e) =>
                      handleLanguageProfilesChange(
                        "Bilingual Positions",
                        e.target.checked
                      )
                    }
                  />
                  <label htmlFor="languageProfile-bilingual">
                    {t.languageProfileBilingual}
                  </label>
                </li>
              </ul>
            </fieldset>
            <ExtractedFieldIndicator
              isExtracted={isFieldExtracted("languageProfilesInterested")}
              t={t}
            />
          </div>
        </div>

        {/* Navigation */}
        <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onPrevious}
            className="w-full sm:w-auto px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {t.previousButton}
          </button>
          <button
            type="submit"
            className="w-full sm:w-auto px-6 py-3 rounded-lg font-medium transition-colors primary-action-button"
          >
            {t.nextEducationButton}
          </button>
        </div>
      </motion.form>
    );
  else
    return (
      <motion.form
        ref={validation.formRef}
        onSubmit={handleSubmit}
        onChangeCapture={validation.handleChangeCapture}
        className="wb-frmvld space-y-20"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        noValidate
      >
        <FormValidationSummary
          t={t}
          errors={validation.errors}
          errorSummaryRef={validation.errorSummaryRef}
          formRef={validation.formRef}
        />
        {/* Section: Language Information */}
        <div className="space-y-8">
          {/* First Official Language */}
          <div>
            <fieldset className="gc-chckbxrdio">
              <legend className="question-legend">
                {requiredQuestionText(
                  <>15. {t.firstOfficialLanguageLabel}</>
                )}
              </legend>
              <p className="text-lg text-gray-700 mb-3">
                {t.firstOfficialLanguageLearnMore}{" "}
                <a
                  href={firstOfficialLanguageUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 hover:text-primary-700 underline visited:text-primary-600"
                  aria-label={`${t.firstOfficialLanguageLearnMoreLink} (opens in new window)`}
                >
                  {t.firstOfficialLanguageLearnMoreLink}
                </a>
              </p>
              <ul className="list-unstyled lst-spcd-2">
                <li className="radio">
                  <input
                    type="radio"
                    name="firstOfficialLanguage"
                    id="firstOfficialLanguage-english-desktop"
                    value="English"
                    checked={enhancedData.firstOfficialLanguage === "English"}
                    onChange={(e) =>
                      handleEnhancedDataChange(
                        "firstOfficialLanguage",
                        e.target.value
                      )
                    }
                    required
                  />
                  <label htmlFor="firstOfficialLanguage-english-desktop">
                    {t.firstOfficialLanguageEnglish}
                  </label>
                </li>
                <li className="radio">
                  <input
                    type="radio"
                    name="firstOfficialLanguage"
                    id="firstOfficialLanguage-french-desktop"
                    value="French"
                    checked={enhancedData.firstOfficialLanguage === "French"}
                    onChange={(e) =>
                      handleEnhancedDataChange(
                        "firstOfficialLanguage",
                        e.target.value
                      )
                    }
                    required
                  />
                  <label htmlFor="firstOfficialLanguage-french-desktop">
                    {t.firstOfficialLanguageFrench}
                  </label>
                </li>
                <li className="radio">
                  <input
                    type="radio"
                    name="firstOfficialLanguage"
                    id="firstOfficialLanguage-other-desktop"
                    value="Other"
                    checked={
                      enhancedData.firstOfficialLanguage === "Other" ||
                      enhancedData.firstOfficialLanguage === "Bilingual"
                    }
                    onChange={(e) =>
                      handleEnhancedDataChange(
                        "firstOfficialLanguage",
                        e.target.value
                      )
                    }
                    required
                  />
                  <label htmlFor="firstOfficialLanguage-other-desktop">
                    {t.firstOfficialLanguageOther}
                  </label>
                </li>
              </ul>
            </fieldset>
            <ExtractedFieldIndicator
              isExtracted={isFieldExtracted("firstOfficialLanguage")}
              t={t}
            />
          </div>

          {/* Valid SLE Results */}
          <div>
            <fieldset className="gc-chckbxrdio">
              <legend className="question-legend">
                {requiredQuestionText(<>16. {t.validSLELabel}</>)}
              </legend>
              <p className="text-lg text-gray-700 mb-3">
                {t.validSLELearnMore}{" "}
                <a
                  href={validSLEUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 hover:text-primary-700 underline visited:text-primary-600"
                  aria-label={`${t.validSLELearnMoreLink} (opens in new window)`}
                >
                  {t.validSLELearnMoreLink}
                </a>
              </p>
              <ul className="list-unstyled lst-spcd-2">
                <li className="checkbox">
                  <input
                    type="checkbox"
                    id="validSLE-reading-desktop"
                    checked={validSLEResults.includes("Reading")}
                    onChange={(e) =>
                      handleSLEResultsChange("Reading", e.target.checked)
                    }
                  />
                  <label htmlFor="validSLE-reading-desktop">
                    {t.validSLEReading}
                  </label>
                </li>
                <li className="checkbox">
                  <input
                    type="checkbox"
                    id="validSLE-writing-desktop"
                    checked={validSLEResults.includes("Writing")}
                    onChange={(e) =>
                      handleSLEResultsChange("Writing", e.target.checked)
                    }
                  />
                  <label htmlFor="validSLE-writing-desktop">
                    {t.validSLEWriting}
                  </label>
                </li>
                <li className="checkbox">
                  <input
                    type="checkbox"
                    id="validSLE-oral-desktop"
                    checked={validSLEResults.includes("Oral")}
                    onChange={(e) =>
                      handleSLEResultsChange("Oral", e.target.checked)
                    }
                  />
                  <label htmlFor="validSLE-oral-desktop">
                    {t.validSLEOral}
                  </label>
                </li>
                <li className="checkbox">
                  <input
                    type="checkbox"
                    id="validSLE-no-desktop"
                    checked={validSLEResults.includes("No")}
                    onChange={(e) =>
                      handleSLEResultsChange("No", e.target.checked)
                    }
                  />
                  <label htmlFor="validSLE-no-desktop">{t.validSLENo}</label>
                </li>
              </ul>
            </fieldset>
            <ExtractedFieldIndicator
              isExtracted={isFieldExtracted("validSLEResults")}
              t={t}
            />
          </div>

          {/* Additional SLE fields if has valid results */}
          {hasValidSLE &&
            !validSLEResults.includes("No") && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                transition={{ duration: 0.3 }}
                className="space-y-8 pt-4"
                aria-live="polite"
                aria-atomic="true"
              >
                {/* SLE Level */}
                <div className="space-y-2">
                  <label
                    htmlFor="sleLevel-desktop"
                    className="block text-lg font-semibold text-gray-900 required"
                  >
                    17. {t.sleLevelLabel}{" "}
                    <strong className="required">{t.required}</strong>
                  </label>
                  {renderFieldHelp("sleLevel-desktop", t.sleLevelPlaceholder)}
                  <input
                    type="text"
                    id="sleLevel-desktop"
                    name="sleLevel"
                    value={enhancedData.sleLevel || ""}
                    onChange={(e) =>
                      handleEnhancedDataChange("sleLevel", e.target.value)
                    }
                    className="form-control"
                    aria-describedby="sleLevel-desktop-help"
                    required={hasActualSLEResults}
                  />
                  <ExtractedFieldIndicator
                    isExtracted={isFieldExtracted("sleLevel")}
                    t={t}
                  />
                </div>

                {/* SLE Expiry Date */}
                <div className="space-y-2">
                  <label
                    htmlFor="sleExpiryDate-desktop"
                    className="block text-lg font-semibold text-gray-900 required"
                  >
                    18. {t.sleExpiryDateLabel}{" "}
                    <strong className="required">{t.required}</strong>
                  </label>
                  <input
                    type="date"
                    id="sleExpiryDate-desktop"
                    name="sleExpiryDate"
                    value={enhancedData.sleExpiryDate || ""}
                    onChange={(e) =>
                      handleEnhancedDataChange("sleExpiryDate", e.target.value)
                    }
                    className="form-control"
                    required={hasActualSLEResults}
                  />
                  <ExtractedFieldIndicator
                    isExtracted={isFieldExtracted("sleExpiryDate")}
                    t={t}
                  />
                </div>
              </motion.div>
            )}

          {/* Language Profiles Interested */}
          <div>
            <fieldset className="gc-chckbxrdio">
              <legend className="question-legend">
                {requiredQuestionText(<>19. {t.languageProfilesLabel}</>)}
              </legend>
              <ul className="list-unstyled lst-spcd-2">
                <li className="checkbox">
                  <input
                    type="checkbox"
                    id="languageProfile-english-desktop"
                    checked={languageProfilesInterested.some((profile) =>
                      profile.toLowerCase().includes("english")
                    )}
                    onChange={(e) =>
                      handleLanguageProfilesChange(
                        "English Essential Positions",
                        e.target.checked
                      )
                    }
                  />
                  <label htmlFor="languageProfile-english-desktop">
                    {t.languageProfileEnglish}
                  </label>
                </li>
                <li className="checkbox">
                  <input
                    type="checkbox"
                    id="languageProfile-french-desktop"
                    checked={languageProfilesInterested.some((profile) =>
                      profile.toLowerCase().includes("french")
                    )}
                    onChange={(e) =>
                      handleLanguageProfilesChange(
                        "French Essential Positions",
                        e.target.checked
                      )
                    }
                  />
                  <label htmlFor="languageProfile-french-desktop">
                    {t.languageProfileFrench}
                  </label>
                </li>
                <li className="checkbox">
                  <input
                    type="checkbox"
                    id="languageProfile-bilingual-desktop"
                    checked={languageProfilesInterested.some((profile) =>
                      profile.toLowerCase().includes("bilingual")
                    )}
                    onChange={(e) =>
                      handleLanguageProfilesChange(
                        "Bilingual Positions",
                        e.target.checked
                      )
                    }
                  />
                  <label htmlFor="languageProfile-bilingual-desktop">
                    {t.languageProfileBilingual}
                  </label>
                </li>
              </ul>
            </fieldset>
            <ExtractedFieldIndicator
              isExtracted={isFieldExtracted("languageProfilesInterested")}
              t={t}
            />
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between pt-6">
          <button
            type="button"
            onClick={onPrevious}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {t.previousButton}
          </button>
          <button
            type="submit"
            className="px-6 py-3 rounded-lg font-medium transition-colors primary-action-button"
          >
            {t.nextEducationButton}
          </button>
        </div>
      </motion.form>
    );
};

export default LanguageStep;
